import { Injectable } from "@angular/core";
import { IWebRTCScreenCaptureDetails } from "../interfaces/webRTC.interface";
import { v4 } from "uuid";
import { IIceServers } from "../interfaces/messages";

export type callStates = 'None' | 'Calling...' | 'Connecting...' | 'Connected';
export type callCloseReasons = 'end' | 'error' | 'agent-declined' | 'agent-aux-change-break' | 'agent-aux-change-loggedout' |
    'supervisor-logout' | 'agent-change' | 'ice-disconnected' | 'ice-signaling-closed' | 'connection-timeout' | 'license-limit-reached' |
    'BARGEIN_DECLINED_ANOTHER_SESSION_IN_PROGRESS' | 'BARGEIN_DECLINED_BY_AGENT' | 'BARGEIN_DECLINED_NO_RESPONSE' | 'BARGEIN_DECLINED_SCREEN_LOCKED' |
    'BARGEIN_DECLINED_SCREEN_BLOCKED' | 'no-screens-available' | 'change-screen-selection' | 'BARGEIN_ENDED_NETWORK_DISCONNECT' | 'navigate-all-agents' |
    'refresh' | 'navigate-dashboard' | 'navigate-gamification' | 'navigate-superadmin' | 'supervisor-tab-close' | 'supervisor-view-hidden' | 'BARGEIN_ENDED_SCREEN_LOCKED' |
    'BARGEIN_ENDED_SCREEN_BLOCKED' | 'BARGEININIT_CONSENT_DECLINED_ANOTHER_CONSENT_IN_PROGRESS';

@Injectable()
export class WebRTCService {
    private currentCallState: callStates = 'None';
    private isVideoTrackReceived: boolean = false;
    private isICEConnectionEstablished: boolean = false;
    private previousICEState: RTCIceConnectionState = 'new';
    private currentICEState: RTCIceConnectionState = 'new';
    private callingAgentLanId: string = '';
    private peerConnectionConfig: RTCConfiguration = {
        iceServers: [],
        iceTransportPolicy: "all",
    }
    private screenShareScreenInfo: IWebRTCScreenCaptureDetails[] = [];
    private selectedScreensForScreenShare: number[] = [];
    private uniqueIdForSession: string = '';
    private endReasons: Map<callCloseReasons, string> = new Map([
        ["end", "Session ended by supervisor"],
        ["error", "Session ended due to unknown server error"],
        ["agent-declined", "Session declined by agent"],
        ["agent-aux-change-break", "Session ended due to agent state change ({status})"],
        ["agent-aux-change-loggedout", "Session ended due to agent state change (logged out)"],
        ["supervisor-logout", "Session ended due to supervisor logout"],
        ["agent-change", "Session ended due to supervisor switching to another agent"],
        ["navigate-all-agents", "Session ended due to supervisor navigating to All Agents view"],
        ["refresh", "Session ended due to supervisor clicking on refresh button"],
        ["navigate-dashboard", "Session ended due to supervisor navigating to Dashboard view"],
        ["navigate-gamification", "Session ended due to supervisor navigating to Gamification view"],
        ["navigate-superadmin", "Session ended due to supervisor navigating to SuperAdmin view"],
        ["supervisor-tab-close", "Session ended due to supervisor closing the browser or browser tab"],
        ["supervisor-view-hidden", "Session ended due to supervisor browser window being minimized or tab switched or browser went background"],
        ["ice-disconnected", "Session ended due to a connectivity issue between supervisor and agent (ICE: {iceState}, Signaling State: {signallingState}, Tracks received: {trackReceived}, Agent Candidate: {agentCandidateInfo}, Sup Candidate: {supervisorCandidateInfo})"],
        ["ice-signaling-closed", ""],
        ["license-limit-reached", "Session failed to start as the maximum number of concurrent screen glance sessions has been reached ({count})."],
        ["connection-timeout", "Session failed to connect due to {timeout}s timeout (ICE: {iceState}, Signaling State: {signallingState}, Tracks received: {trackReceived}, Agent Candidate: {agentCandidateInfo}, Sup Candidate: {supervisorCandidateInfo})"],
        ["BARGEIN_DECLINED_ANOTHER_SESSION_IN_PROGRESS", "Agent {agentLanId} is on a screen glance session with another supervisor."],
        ["BARGEIN_DECLINED_BY_AGENT", "Agent {agentLanId} declined consent to the screen glance session."],
        ["BARGEIN_DECLINED_NO_RESPONSE", "Agent {agentLanId} is not responding to the screen glance session request."],
        ["BARGEIN_DECLINED_SCREEN_LOCKED", "Agent {agentLanId} screen is currently locked."],
        ["BARGEIN_DECLINED_SCREEN_BLOCKED", "Agent {agentLanId} screen is currently screen blocked."],
        ["no-screens-available", "Session could not start as agent has no screens to share."],
        ["change-screen-selection", "Supervisor changed screens ({count})."],
        ["BARGEIN_ENDED_NETWORK_DISCONNECT", "Session ended from SWFH UI (Error: {errorCode}, ICE: {iceState}, Signaling State: {signallingState}, Tracks received: {trackReceived}, Agent Candidate: {agentCandidateInfo}, Sup Candidate: {supervisorCandidateInfo})"],
        ["BARGEIN_ENDED_SCREEN_LOCKED", "Screen Glance Ended Because Screen Is Locked"],
        ["BARGEIN_ENDED_SCREEN_BLOCKED", "Screen Glance Ended Because Screen Is Blocked"],
        ["BARGEININIT_CONSENT_DECLINED_ANOTHER_CONSENT_IN_PROGRESS", "Agent {agentLanId} has started a screen glance session with another supervisor."],
    ]);
    private endCodes: Map<callCloseReasons, string> = new Map([
        ["end", "SUPERVISOR_END"],
        ["error", ""],
        ["agent-declined", "AGENT_DECLINED"],
        ["agent-aux-change-break", "AGENT_AUX_CODE_CHANGE"],
        ["agent-aux-change-loggedout", "AGENT_LOGGED_OUT"],
        ["supervisor-logout", "SUPERVISOR_LOGOUT"],
        ["agent-change", "SUPERVISOR_CHANGED_AGENT"],
        ["navigate-all-agents", "SUPERVISOR_NAVIGATE_ALL_AGENT_VIEW"],
        ["refresh", "SUPERVISOR_REFRESH"],
        ["navigate-dashboard", "SUPERVISOR_NAVIGATE_DASHBOARD"],
        ["navigate-gamification", "SUPERVISOR_NAVIGATE_GAMIFICATION"],
        ["navigate-superadmin", "SUPERVISOR_NAVIGATE_SUPERADMIN"],
        ["supervisor-tab-close", "SUPERVISOR_TAB_CLOSE"],
        ["supervisor-view-hidden", "SUPERVISOR_VIEW_HIDDEN"],
        ["ice-disconnected", "SUPERVISOR_ICE_DISCONNECT"],
        ["ice-signaling-closed", ""],
        ["license-limit-reached", ""],
        ["connection-timeout", "BARGEIN_ENDED_NETWORK_DISCONNECT_SUPERVISOR"],
        ["BARGEIN_DECLINED_ANOTHER_SESSION_IN_PROGRESS", ""],
        ["BARGEIN_DECLINED_BY_AGENT", ""],
        ["BARGEIN_DECLINED_NO_RESPONSE", ""],
        ["BARGEIN_DECLINED_SCREEN_LOCKED", ""],
        ["BARGEIN_DECLINED_SCREEN_BLOCKED", ""],
        ["no-screens-available", ""],
        ["change-screen-selection", ""],
        ["BARGEIN_ENDED_NETWORK_DISCONNECT", ""],
        ["BARGEININIT_CONSENT_DECLINED_ANOTHER_CONSENT_IN_PROGRESS", ""],
    ]);
    private isBargeInInitRequestSent: boolean = false;

    updateCallState = (callStatus: callStates): void => { this.currentCallState = callStatus }

    getCallState = (): callStates => this.currentCallState;

    isCallInProgress = (): boolean => this.currentCallState !== 'None';

    updateCallingAgentLanId = (lanId: string): void => { this.callingAgentLanId = lanId }

    getCallingAgentLanId = (): string => this.callingAgentLanId;

    updatePeerConnectionConfig = (iceServers: IIceServers[]): void => {
        if (iceServers != null && iceServers.length > 0) {
            this.peerConnectionConfig.iceServers = [];
            iceServers.forEach((server: IIceServers) => {
                this.peerConnectionConfig.iceServers?.push({
                    urls: server.Urls,
                    username: server.TurnUsername,
                    credential: server.TurnPassword,
                });
            });
        }
    }

    getPeerConnectionConfig = (): RTCConfiguration => this.peerConnectionConfig;

    isAgentOnCall = (agentLanId: string): boolean => this.currentCallState !== 'None' && this.callingAgentLanId.toLowerCase() === agentLanId.toLowerCase()

    updateScreenShareScreenInfo = (deviceInfo: IWebRTCScreenCaptureDetails[]): void => { this.screenShareScreenInfo = deviceInfo }

    clearScreenShareScreenInfo = (): void => {
        this.screenShareScreenInfo = [];
        this.selectedScreensForScreenShare = [];
        this.isVideoTrackReceived = false;
        this.isICEConnectionEstablished = false;
        this.uniqueIdForSession = '';
        this.previousICEState = 'new';
        this.currentICEState = 'new';
        this.isBargeInInitRequestSent = false;
    }

    updateSelectedScreenForScreenShare = (screenIds: number[]): void => { this.selectedScreensForScreenShare = screenIds; }

    getSelectedScreensForScreenShare = (): number[] => this.selectedScreensForScreenShare;

    getScreenShareDetails = (): IWebRTCScreenCaptureDetails[] => this.screenShareScreenInfo;

    updateIsVideoTrackReceived = (isReceived: boolean): void => { this.isVideoTrackReceived = isReceived; }

    getIsVideoTrackReceived = (): boolean => this.isVideoTrackReceived;

    updateIsICEConnectionEstablished = (isEstablished: boolean): void => { this.isICEConnectionEstablished = isEstablished; }

    getIsICEConnectionEstablished = (): boolean => this.isICEConnectionEstablished;

    getUniqueIdForSession = (forceClearPreviousSession: boolean = false): string => {
        if (this.uniqueIdForSession === '' || forceClearPreviousSession === true)
            this.uniqueIdForSession = v4();

        return this.uniqueIdForSession;
    }

    generateEndReason = (reasonKey: callCloseReasons, data: Record<string, string> = {}): string => {
        const reasonTemplate: string = this.endReasons.get(reasonKey) ?? ``;
        return this.fill(reasonTemplate, data);
    }

    generateAuditEndReason = (reasonKey: callCloseReasons, data: Record<string, string> = {}): string => {
        const reasonTemplate: string = this.endReasons.get(reasonKey) ?? ``;
        const finalMessage = this.fill(reasonTemplate, data);

        return `${this.getUniqueIdForSession()}|${finalMessage}`;
    }

    private fill(template: string, data: Record<string, string>): string {
        return template.replace(/\{(\w+)\}/g, (_, key) => data[key] ?? "");
    }

    updateICETransportPolicy = (policy: RTCIceTransportPolicy): void => {
        this.peerConnectionConfig.iceTransportPolicy = policy;
    }

    generateEndCode = (reasonKey: callCloseReasons): string => this.endCodes.get(reasonKey) ?? ``;

    updateICEConnectivityState = (state: RTCIceConnectionState): void => {
        if (this.currentICEState !== state) {
            this.previousICEState = this.currentICEState;
            this.currentICEState = state;
        }
    }

    getCurrentICEConnectivityState = (): RTCIceConnectionState => this.currentICEState;
    getPreviousICEConnectivityState = (): RTCIceConnectionState => this.previousICEState;

    updateIsBargeInInitRequestSent = (isSent: boolean): void => { this.isBargeInInitRequestSent = isSent; }

    getIsBargeInInitRequestSent = (): boolean => this.isBargeInInitRequestSent;
}